(function (window, document) {
  'use strict';

  if (window.VenadaHeroSequence) {
    window.VenadaHeroSequence.initAll();
    return;
  }

  var ROOT_SELECTOR = '[data-venada-hero-sequence]';
  var instances = new WeakMap();

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function frameIndexForProgress(progress, frameCount) {
    var count = Math.max(1, Math.floor(Number(frameCount) || 1));
    return Math.round(clamp(Number(progress) || 0, 0, 1) * (count - 1));
  }

  function frameWindow(center, total, radius) {
    var count = Math.floor(Number(total) || 0);
    var windowRadius = radius === undefined ? 4 : Math.floor(Number(radius));
    if (count <= 0 || windowRadius < 0) return [];
    var boundedCenter = clamp(Math.floor(Number(center) || 0), 0, count - 1);
    var start = Math.max(0, boundedCenter - windowRadius);
    var end = Math.min(count - 1, boundedCenter + windowRadius);
    var indexes = [];
    for (var index = start; index <= end; index += 1) indexes.push(index);
    return indexes;
  }

  function drawCover(context, image, width, height) {
    var scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    var sourceWidth = width / scale;
    var sourceHeight = height / scale;
    var sourceX = (image.naturalWidth - sourceWidth) / 2;
    var sourceY = (image.naturalHeight - sourceHeight) / 2;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);
  }

  function presentationFor(viewportWidth, reducedMotion) {
    if (reducedMotion) return 'static-poster';
    return viewportWidth <= 767 ? 'mobile-crossfade' : 'desktop-sequence';
  }

  function addMediaListener(mediaQuery, callback) {
    if (!mediaQuery) return function () {};
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback);
      return function () { mediaQuery.removeEventListener('change', callback); };
    }
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(callback);
      return function () { mediaQuery.removeListener(callback); };
    }
    return function () {};
  }

  function parseFrameUrls(root) {
    var data = root.querySelector('[data-venada-hero-frame-urls]');
    if (!data) return [];
    try {
      var urls = JSON.parse(data.textContent || '[]');
      return Array.isArray(urls) ? urls.filter(function (url) { return typeof url === 'string' && url.length > 0; }) : [];
    } catch (error) {
      return [];
    }
  }

  function fallbackScrollProgress(section, callback) {
    var frameId = null;
    var stopped = false;

    function update() {
      frameId = null;
      if (stopped) return;
      var rect = section.getBoundingClientRect();
      var viewportHeight = window.innerHeight || 0;
      var distance = rect.height - viewportHeight;
      var progress = distance > 0 ? -rect.top / distance : 0;
      callback(clamp(progress, 0, 1));
    }

    function schedule() {
      if (stopped || frameId !== null) return;
      frameId = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();

    return function () {
      stopped = true;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = null;
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }

  function createInstance(root) {
    var canvas = root.querySelector('[data-venada-hero-canvas]');
    var context = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    var frameUrls = parseFrameUrls(root);
    var mobileFrames = Array.prototype.slice.call(root.querySelectorAll('[data-venada-hero-mobile-frame]'));
    var compactQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 767px)') : null;
    var reducedQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var cached = new Map();
    var pending = new Map();
    var activeWindow = new Set();
    var requestedIndex = 0;
    var lastImage = null;
    var resizeFrameId = null;
    var stopScroll = function () {};
    var removeFirstScroll = function () {};
    var removeDeferredMobileLoad = function () {};
    var removeResize = function () {};
    var sequenceSubscribed = false;
    var destroyed = false;

    function releaseImage(image) {
      if (!image) return;
      if (image === lastImage) {
        lastImage = null;
        root.removeAttribute('data-venada-hero-canvas-ready');
        if (context && canvas) context.clearRect(0, 0, canvas.width, canvas.height);
      }
      image.onload = null;
      image.onerror = null;
      image.src = '';
    }

    function clearSequence() {
      stopScroll();
      stopScroll = function () {};
      sequenceSubscribed = false;
      removeFirstScroll();
      removeFirstScroll = function () {};
      removeResize();
      removeResize = function () {};
      if (resizeFrameId !== null) window.cancelAnimationFrame(resizeFrameId);
      resizeFrameId = null;
      pending.forEach(function (record) { releaseImage(record.image); });
      cached.forEach(releaseImage);
      pending.clear();
      cached.clear();
      activeWindow.clear();
      lastImage = null;
      root.removeAttribute('data-venada-hero-canvas-ready');
      if (context && canvas) context.clearRect(0, 0, canvas.width, canvas.height);
    }

    function unloadMobileFrames() {
      removeDeferredMobileLoad();
      removeDeferredMobileLoad = function () {};
      mobileFrames.forEach(function (frame) {
        frame.removeAttribute('src');
        frame.removeAttribute('srcset');
      });
    }

    function loadMobileFrame(frame) {
      if (!frame) return;
      if (frame.dataset.srcset) frame.srcset = frame.dataset.srcset;
      if (frame.dataset.src) frame.src = frame.dataset.src;
    }

    function loadMobileFrames() {
      if (mobileFrames.length === 0) return;
      loadMobileFrame(mobileFrames[0]);
      if (mobileFrames.length === 1) return;

      function loadRemainingFrames() {
        removeDeferredMobileLoad();
        mobileFrames.slice(1).forEach(loadMobileFrame);
      }

      if (document.readyState === 'complete') {
        loadRemainingFrames();
        return;
      }

      window.addEventListener('load', loadRemainingFrames);
      removeDeferredMobileLoad = function () {
        window.removeEventListener('load', loadRemainingFrames);
        removeDeferredMobileLoad = function () {};
      };
    }

    function resizeCanvas() {
      if (!canvas || !context) return;
      var ratio = Math.max(1, Number(window.devicePixelRatio) || 1);
      var width = Math.max(1, Math.round((canvas.clientWidth || 1920) * ratio));
      var height = Math.max(1, Math.round((canvas.clientHeight || 1080) * ratio));
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
      if (lastImage && lastImage.naturalWidth > 0 && lastImage.naturalHeight > 0) {
        drawCover(context, lastImage, width, height);
      } else if (lastImage) {
        releaseImage(lastImage);
      }
    }

    function scheduleResize() {
      if (resizeFrameId !== null) return;
      resizeFrameId = window.requestAnimationFrame(function () {
        resizeFrameId = null;
        resizeCanvas();
      });
    }

    function loadFrame(index) {
      var loaded = cached.get(index);
      if (loaded) {
        if (index === requestedIndex) {
          lastImage = loaded;
          drawCover(context, loaded, canvas.width, canvas.height);
          root.setAttribute('data-venada-hero-canvas-ready', 'true');
        }
        return;
      }
      if (pending.has(index) || !frameUrls[index]) return;

      var image = new window.Image();
      var record = { image: image };
      pending.set(index, record);
      image.decoding = 'async';
      image.onload = function () {
        image.onload = null;
        image.onerror = null;
        pending.delete(index);
        if (destroyed || !activeWindow.has(index)) {
          releaseImage(image);
          return;
        }
        cached.set(index, image);
        if (index === requestedIndex) {
          lastImage = image;
          drawCover(context, image, canvas.width, canvas.height);
          root.setAttribute('data-venada-hero-canvas-ready', 'true');
        }
      };
      image.onerror = function () {
        pending.delete(index);
        releaseImage(image);
      };
      image.src = frameUrls[index];
    }

    function renderProgress(progress) {
      if (destroyed || !canvas || !context || frameUrls.length === 0) return;
      var index = frameIndexForProgress(progress, frameUrls.length);
      requestedIndex = index;
      activeWindow = new Set(frameWindow(index, frameUrls.length, 4));

      cached.forEach(function (image, cachedIndex) {
        if (activeWindow.has(cachedIndex)) return;
        cached.delete(cachedIndex);
        releaseImage(image);
      });
      pending.forEach(function (record, pendingIndex) {
        if (activeWindow.has(pendingIndex)) return;
        pending.delete(pendingIndex);
        releaseImage(record.image);
      });

      loadFrame(index);
      activeWindow.forEach(function (neighbor) {
        if (neighbor !== index) loadFrame(neighbor);
      });
    }

    function startSequence() {
      if (!canvas || !context || frameUrls.length === 0) return;
      resizeCanvas();
      window.addEventListener('resize', scheduleResize);
      removeResize = function () { window.removeEventListener('resize', scheduleResize); };
      var trackProgress = typeof window.venadaScrollProgress === 'function' ? window.venadaScrollProgress : fallbackScrollProgress;

      function subscribeToProgress() {
        if (destroyed || sequenceSubscribed) return;
        removeFirstScroll();
        removeFirstScroll = function () {};
        sequenceSubscribed = true;
        stopScroll = trackProgress(root, renderProgress);
      }

      var rect = typeof root.getBoundingClientRect === 'function' ? root.getBoundingClientRect() : null;
      if (rect && rect.top < 0) {
        subscribeToProgress();
      } else {
        var onFirstScroll = function () { subscribeToProgress(); };
        window.addEventListener('scroll', onFirstScroll, { passive: true });
        removeFirstScroll = function () { window.removeEventListener('scroll', onFirstScroll); };
      }
    }

    function currentPresentation() {
      var isReduced = reducedQuery ? reducedQuery.matches : false;
      var isCompact = compactQuery ? compactQuery.matches : (window.innerWidth || 0) <= 767;
      return presentationFor(isCompact ? 767 : 768, isReduced);
    }

    function applyPresentation() {
      if (destroyed) return;
      clearSequence();
      unloadMobileFrames();
      var presentation = currentPresentation();
      root.setAttribute('data-venada-hero-presentation', presentation);
      if (presentation === 'desktop-sequence') startSequence();
      if (presentation === 'mobile-crossfade') loadMobileFrames();
    }

    var removeCompactListener = addMediaListener(compactQuery, applyPresentation);
    var removeReducedListener = addMediaListener(reducedQuery, applyPresentation);
    applyPresentation();

    return {
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        clearSequence();
        unloadMobileFrames();
        removeCompactListener();
        removeReducedListener();
        root.removeAttribute('data-venada-hero-presentation');
      }
    };
  }

  function initRoot(root) {
    if (!root) return null;
    var existing = instances.get(root);
    if (existing) return existing;
    var instance = createInstance(root);
    instances.set(root, instance);
    return instance;
  }

  function destroyRoot(root) {
    var instance = root && instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    var searchRoot = scope || document;
    Array.prototype.forEach.call(searchRoot.querySelectorAll(ROOT_SELECTOR), initRoot);
    if (searchRoot.matches && searchRoot.matches(ROOT_SELECTOR)) initRoot(searchRoot);
  }

  function destroyAll(scope) {
    var searchRoot = scope || document;
    Array.prototype.forEach.call(searchRoot.querySelectorAll(ROOT_SELECTOR), destroyRoot);
    if (searchRoot.matches && searchRoot.matches(ROOT_SELECTOR)) destroyRoot(searchRoot);
  }

  window.VenadaHeroSequence = {
    frameIndexForProgress: frameIndexForProgress,
    frameWindow: frameWindow,
    drawCover: drawCover,
    presentationFor: presentationFor,
    initRoot: initRoot,
    destroyRoot: destroyRoot,
    initAll: initAll,
    destroyAll: destroyAll
  };

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyAll(event.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); }, { once: true });
  } else {
    initAll();
  }
})(window, document);
